import { FormEvent, useEffect, useState } from 'react';
import type { MeResponse } from '@kassa/shared';
import { api, ApiError } from '../api';
import { Signup } from './Signup';

interface Props {
  onLoggedIn: (me: MeResponse) => void;
}

// Veb girişi: telefon və ya e-poçt + parol. Kassir PİN-i masaüstü proqramındadır (bu mərhələdə yoxdur).
export function Login({ onLoggedIn }: Props) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [setupCode] = useState(readSetupHash);
  const [resetCode] = useState(() => readLinkHash('reset'));
  const [mode, setMode] = useState<'login' | 'setup' | 'forgot' | 'reset' | 'signup'>(setupCode ? 'setup' : resetCode ? 'reset' : 'login');
  const [notice, setNotice] = useState<string | null>(null);
  // «Google ilə daxil ol» (KT-42): düymə yalnız server funksiyanı açıq elan edəndə görünür
  const [googleOn, setGoogleOn] = useState(false);
  // Google geri dönüşünün xətası: ?google=<kod> (KT-42). Ünvan təmizlənir ki, yenidən göstərilməsin.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('google');
    if (!code) return;
    const text: Record<string, string> = {
      google_no_account: 'Bu Google e-poçtu ilə hesab yoxdur. Sahibkar hesabınızı əlavə etməlidir.',
      google_state: 'Google girişi vaxtı keçib və ya səhv açıldı. Yenidən cəhd edin.',
      google_token: 'Google girişi təsdiqlənmədi. Yenidən cəhd edin.',
      google_unavailable: 'Google hazırda əlçatmazdır. Parolla daxil olun.',
      access_denied: 'Bu Google hesabına hələ icazə verilməyib.',
      // Dəvətlə Google qeydiyyatı (KT-84)
      account_exists: 'Bu e-poçtla hesab artıq var — parolla və ya Google ilə daxil olun. Yeni şirkəti admin hesabınıza əlavə edəcək.',
      google_email_mismatch: 'Google e-poçtu dəvətin e-poçtu ilə uyğun deyil. Dəvət gələn e-poçtun Google hesabını seçin.',
      invite_invalid: 'Dəvət linki keçərsizdir və ya vaxtı bitib. Adminə yazın.',
    };
    setNotice(Object.prototype.hasOwnProperty.call(text, code) ? text[code] : 'Google ilə giriş alınmadı.');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  // «Pulsuz sınaq» (KT-85): yalnız admin öz qeydiyyatını açıbsa
  const [signup, setSignup] = useState<{ enabled: boolean; trialDays: number } | null>(null);
  useEffect(() => {
    api
      .signupOpen()
      .then(setSignup)
      .catch(() => setSignup(null));
  }, []);
  useEffect(() => {
    fetch('/api/auth/google/enabled', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((b: { enabled: boolean }) => setGoogleOn(b.enabled))
      .catch(() => setGoogleOn(false));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLoggedIn(await api.login({ identifier, password, remember }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <aside className="auth-brand">
        <div className="brand-row">
          <svg width="34" height="34" viewBox="0 0 28 28" aria-hidden="true">
            <rect x="2" y="10" width="24" height="14" rx="3" fill="none" stroke="#FFFFFF" strokeWidth="2" />
            <rect x="2" y="5" width="24" height="4" rx="2" fill="#E9A23B" />
          </svg>
          <span className="brand-name">piştaxta</span>
        </div>
        <h1 className="brand-title">Kassa istənilən kompüterdə.<br />Məlumat serverdə.</h1>
        <p className="brand-text">
          Sahib və mühasib eyni hesabla brauzerdən daxil ola bilər. Kassa kompüterində proqram ayrıca işləyir.
        </p>
      </aside>

      <section className="auth-form-wrap">
        {mode === 'signup' && signup?.enabled ? (
          <Signup trialDays={signup.trialDays} onBack={() => setMode('login')} />
        ) : mode === 'forgot' ? (
          <ForgotPassword initialIdentifier={identifier} onBack={() => setMode('login')} />
        ) : mode === 'reset' ? (
          <ResetPassword
            code={resetCode}
            onDone={() => {
              setMode('login');
              setNotice('Parol yeniləndi. Bütün cihazlardakı köhnə girişlər bağlandı — yeni parolla daxil olun.');
            }}
            onCancel={() => setMode('login')}
          />
        ) : mode === 'setup' ? (
          <SetupPassword
            initialCode={setupCode}
            onDone={() => {
              setMode('login');
              setNotice('Parol qoyuldu. İndi telefon və ya e-poçt və yeni parolla daxil olun.');
            }}
            onCancel={() => setMode('login')}
          />
        ) : (
        <form className="auth-form" onSubmit={submit} noValidate>
          <div className="auth-heading">
            <h2>Daxil olun</h2>
            <span className="muted">Telefon, e-poçt və ya login ilə</span>
          </div>

          <label className="field">
            E-poçt, telefon və ya login
            <input
              name="identifier"
              type="text"
              autoComplete="username"
              placeholder="+994 50 000 00 00 və ya login"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={busy}
            />
          </label>

          <label className="field">
            Parol
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={busy}
            />
          </label>

          <label className="check-row">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={busy} />
            Bu cihazı yadda saxla (30 gün)
          </label>

          {notice && <p className="ok">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn-primary" disabled={busy || !identifier || !password}>
            {busy ? 'Yoxlanılır…' : 'Daxil ol'}
          </button>
          {googleOn && (
            <a className="btn-secondary" href="/api/auth/google/start">
              Google ilə daxil ol
            </a>
          )}
          <button type="button" className="link-btn" onClick={() => setMode('forgot')}>
            Parolu unutdum
          </button>
          <button type="button" className="link-btn" onClick={() => setMode('setup')}>
            Açar kodum var — ilk parolu qoy
          </button>
          {signup?.enabled && (
            <button type="button" className="btn-secondary" onClick={() => setMode('signup')}>
              Hesabınız yoxdur? Pulsuz sınaq — {signup.trialDays} gün
            </button>
          )}
        </form>
        )}
      </section>
    </div>
  );
}

// Sahibin ilk parolu: admin verdiyi birdəfəlik açar kodu (24 saat) ilə.
function SetupPassword({ initialCode, onDone, onCancel }: { initialCode: string; onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState(initialCode);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) {
      setError('Parollar eyni deyil');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setupPassword({ code, password });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      <div className="auth-heading">
        <h2>İlk parol</h2>
        <span className="muted">Açar kodunu sizə platforma komandası verib (24 saat keçərlidir)</span>
      </div>
      <label className="field">
        Açar kodu
        <input
          name="code"
          type="text"
          autoComplete="one-time-code"
          placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          disabled={busy}
        />
      </label>
      <label className="field">
        Yeni parol (ən azı 8 simvol)
        <input name="new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        Yeni parol (təkrar)
        <input name="repeat-password" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required disabled={busy} />
      </label>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={busy || !code || password.length < 8 || !repeat}>
        {busy ? 'Yoxlanılır…' : 'Parolu qoy'}
      </button>
      <button type="button" className="link-btn" onClick={onCancel}>
        Girişə qayıt
      </button>
    </form>
  );
}

// Bərpa/açar linki: https://…/#setup=KOD. Kod fraqmentdədir (serverə getmir); oxunandan sonra ünvandan silinir.
function readSetupHash(): string {
  const m = /^#setup=([A-Za-z0-9-]{16,40})$/.exec(window.location.hash);
  if (!m) return '';
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return m[1];
}

/** Link kodu: https://…/#reset=KOD və ya #verify=KOD (KT-75). Oxunandan sonra ünvandan silinir. */
export function readLinkHash(param: 'reset' | 'verify' | 'qeydiyyat'): string {
  const m = new RegExp(`^#${param}=([A-Za-z0-9_-]{43})$`).exec(window.location.hash);
  if (!m) return '';
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return m[1];
}

// «Parolu unutdum» (KT-75): cavab həmişə eynidir — hesabın olub-olmaması bilinmir.
function ForgotPassword({ initialIdentifier, onBack }: { initialIdentifier: string; onBack: () => void }) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDone((await api.passwordResetRequest(identifier)).message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      <div className="auth-heading">
        <h2>Parolu unutdum</h2>
        <span className="muted">Təsdiqlənmiş e-poçtunuza 30 dəqiqəlik link göndərəcəyik</span>
      </div>
      {done ? (
        <p className="ok" role="status">
          {done}
        </p>
      ) : (
        <>
          <label className="field">
            E-poçt, telefon və ya login
            <input name="identifier" type="text" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required disabled={busy} />
          </label>
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy || !identifier.trim()}>
            {busy ? 'Göndərilir…' : 'Link göndər'}
          </button>
        </>
      )}
      <button type="button" className="link-btn" onClick={onBack}>
        Girişə qayıt
      </button>
    </form>
  );
}

// E-poçtdakı linkdən: yeni parol (KT-75). Uğurda bütün köhnə sessiyalar bağlanır.
function ResetPassword({ code, onDone, onCancel }: { code: string; onDone: () => void; onCancel: () => void }) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) {
      setError('Parollar eyni deyil');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.passwordResetConfirm(code, password);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      <div className="auth-heading">
        <h2>Yeni parol</h2>
        <span className="muted">Link bir dəfə işləyir. Yeni paroldan sonra bütün cihazlarda yenidən daxil olmalısınız.</span>
      </div>
      <label className="field">
        Yeni parol (ən azı 8 simvol)
        <input name="new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        Yeni parol (təkrar)
        <input name="repeat-password" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required disabled={busy} />
      </label>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn-primary" disabled={busy || password.length < 8 || !repeat}>
        {busy ? 'Yoxlanılır…' : 'Parolu yenilə'}
      </button>
      <button type="button" className="link-btn" onClick={onCancel}>
        Girişə qayıt
      </button>
    </form>
  );
}
